import { apiClient } from "../../Utils/apiClient";

export interface UserAttribute {
  id: number | null;
  userId: number | null;
  planAttributeId: number;
  planAttributeName: string;
  planAttributeDesc: string;
  customerAttributeId: number | null;
  defaultValue: string;
  value: string;
  valueType: string;
  usedValue: string | null;
  isTrial: boolean;
  isActive: boolean;
  assignDate: string | null;
  expireDate?: string | null;
}

export interface UserFeaturesParams {
  firstName?: string;
  lastName?: string;
  primaryDIDNumber?: string;
  extension?: string;
  userGroup?: string;
  page?: string;
  pageSize?: string;
  orderBy?: string;
  orderDir?: string;
  custId: string;
}

export interface PlanDetail {
  id: number;
  name: string;
  description: string;
  planId: number;
  icon?: React.ReactElement;
  userAttributes: UserAttribute[];
}

export interface UserFeaturesData {
  planDetails: PlanDetail[];
}

class UserManagementClass {
  async getActivatedLicenses(
    accessToken: string,
    sessionToken: string,
    customerLicenseId: number
  ): Promise<any> {
    return apiClient.get(
      `get-all-activated-user-licenses/${customerLicenseId}`,
      accessToken,
      sessionToken
    );
  }

  async sendCredentialsEmail(
    accessToken: string,
    sessionToken: string,
    params: { userId: string }
  ) {
    return apiClient.post(
      `send-welcome-email/${params.userId}`,
      accessToken,
      sessionToken
    );
  }

  async resetStrettoPassword(
    accessToken: string,
    sessionToken: string,
    params: { userId: string }
  ) {
    return apiClient.post(
      `reset-stretto-password/${params.userId}`,
      accessToken,
      sessionToken
    );
  }

  async updateSMSLicense(
    accessToken: string,
    sessionToken: string,
    params: {
      customerLicenseId: number;
      userId: number;
      SMSEnabled: boolean;
    }
  ): Promise<any> {
    return apiClient.post(
      "enable-ucaas-sms",
      accessToken,
      sessionToken,
      params
    );
  }

  async moveUserLicense(
    accessToken: string,
    sessionToken: string,
    params: {
      userId: number;
      customerLicenseIdOld: number;
      customerLicenseIdNew: number;
    }
  ): Promise<any> {
    return apiClient.post(
      "move-user-to-different-license",
      accessToken,
      sessionToken,
      params
    );
  }

  async transferUserLicense(
    accessToken: string,
    sessionToken: string,
    params: {
      userId: number;
      custId: number;
      deactivateLicense: string;
      activateLicense: string;
    }
  ): Promise<any> {
    return apiClient.post(
      "transfer-user-license",
      accessToken,
      sessionToken,
      params
    );
  }

  async getLicensesByUserId(
    accessToken: string,
    sessionToken: string,
    params: {
      userId: string;
    }
  ): Promise<any> {
    return apiClient.get(
      `get-all-licenses-by-userId/${params.userId}`,
      accessToken,
      sessionToken
    );
  }

  async deleteCustomerLicense(
    accessToken: string,
    sessionToken: string,
    params: {
      id: string;
    }
  ) {
    return apiClient.delete(
      `delete-customer-license/${params.id}`,
      accessToken,
      sessionToken
    );
  }

  async getLicenseTypes(
    accessToken: string,
    sessionToken: string
  ): Promise<any> {
    return apiClient.get("get-all-license-types", accessToken, sessionToken);
  }

  async createNewTrialLicense(
    accessToken: string,
    sessionToken: string,
    params: {
      custId: number;
      licenseId: number;
      value: string;
      usedValue: string;
      userCreated?: string;
      assignDate: string;
      lastOffDate: string;
      isActive: boolean;
      type: string;
      userLicenses?: any[];
    }
  ): Promise<any> {
    return apiClient.post(
      "create-customer-license",
      accessToken,
      sessionToken,
      params
    );
  }

  async activateUserLicense(
    accessToken: string,
    sessionToken: string,
    params: {
      userId: number;
      customerLicenseId: number;
    }
  ): Promise<any> {
    return apiClient.post(
      "create-user-license",
      accessToken,
      sessionToken,
      params
    );
  }

  async deactivateUserLicense(
    accessToken: string,
    sessionToken: string,
    params: {
      id: string;
    }
  ): Promise<any> {
    return apiClient.delete(
      `delete-user-license/${params.id}`,
      accessToken,
      sessionToken
    );
  }

  //Function to get all user licenses by cust_id and customerLicenceId
  async getAllUserLicensesForCustomer(
    accessToken: string,
    sessionToken: string,
    params: {
      custId?: string;
      customerLicenseId?: string;
      LoginName?: string;
      id?: string;
      page?: string;
      pageSize?: string;
      status?: string;
    }
  ): Promise<any> {
    return apiClient.get(
      "get-all-user-licenses-for-customer",
      accessToken,
      sessionToken,
      params
    );
  }

  //Function to get all users by customer id
  async getAllUsersByCustomerId(
    accessToken: string,
    userToken: string,
    params: {
      firstName?: string;
      lastName?: string;
      primaryDIDNumber?: string;
      extension?: string;
      userGroup?: string;
      page?: string;
      pageSize?: string;
      orderBy?: string;
      orderDir?: string;
      custId: string;
    }
  ): Promise<any> {
    const { custId, ...queryParams } = params;
    return apiClient.get(
      `get-all-users-by-custId/${custId}`,
      accessToken,
      userToken,
      queryParams
    );
  }

  async getAllLicencesByCustId(
    accessToken: string,
    sessionToken: string,
    params: {
      custId: string;
    }
  ): Promise<any> {
    return apiClient.get(
      `get-all-licenses/${params.custId}`,
      accessToken,
      sessionToken
    );
  }

  // function to get all customers
  async getAllCustomers(
    accessToken: string,
    sessionToken: string,
    params: {}
  ): Promise<any> {
    return apiClient.get(
      "get-all-customers",
      accessToken,
      sessionToken,
      params
    );
  }

  async getCustomerFeatures(
    accessToken: string,
    sessionToken: string,
    params: {
      custId: string;
    }
  ): Promise<any> {
    return apiClient.get(
      `get-customer-features/${params.custId}`,
      accessToken,
      sessionToken
    );
  }

  //function to get customer
  async getCustomer(
    accessToken: string,
    userToken: string,
    params: {
      id: string;
    }
  ): Promise<any> {
    return apiClient.get(
      `get-customer-by-id/${params.id}`,
      accessToken,
      userToken
    );
  }

  async getUserAttributes(
    accessToken: string,
    userToken: string,
    userId: number
  ): Promise<any> {
    return apiClient.get(
      `get-user-attributes/${userId}`,
      accessToken,
      userToken
    );
  }
}

const UserManagementFunction = new UserManagementClass();
export default UserManagementFunction;
